import React, { useState, useEffect, useCallback } from 'react';
import { MessageSquarePlus, Plus } from 'lucide-react';
import CardBasic from '../ui/Card.jsx';
import FeedbacksTabs from './FeedbacksTabs.jsx';
import FeedbackDetail from './FeedbackDetail.jsx';
import CreateFeedbackModal from './CreateFeedbackModal.jsx';
import Alert from '../ui/Alert.jsx';

const FeedbacksIndex = () => {
    const [viewMode, setViewMode] = useState('list'); // 'list' or 'detail'
    const [selectedFeedbackId, setSelectedFeedbackId] = useState(null);
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [alert, setAlert] = useState({ message: '', type: '' });
    const [refreshFn, setRefreshFn] = useState(null);

    // Check URL for detail parameter on mount
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const detailId = urlParams.get('detail');
        if (detailId) {
            setSelectedFeedbackId(parseInt(detailId, 10));
            setViewMode('detail');
        }
    }, []);

    // Update URL when view mode changes
    useEffect(() => {
        if (viewMode === 'detail' && selectedFeedbackId) {
            const url = new URL(window.location);
            url.searchParams.set('detail', selectedFeedbackId);
            window.history.pushState({}, '', url);
        } else if (viewMode === 'list') {
            const url = new URL(window.location);
            url.searchParams.delete('detail');
            window.history.pushState({}, '', url);
        }
    }, [viewMode, selectedFeedbackId]);

    // Handle browser back/forward
    useEffect(() => {
        const handlePopState = () => {
            const urlParams = new URLSearchParams(window.location.search);
            const detailId = urlParams.get('detail');
            if (detailId) {
                setSelectedFeedbackId(parseInt(detailId, 10));
                setViewMode('detail');
            } else {
                setViewMode('list');
                setSelectedFeedbackId(null);
            }
        };

        window.addEventListener('popstate', handlePopState);
        return () => {
            window.removeEventListener('popstate', handlePopState);
        };
    }, []);

    const handleViewDetail = useCallback((feedbackId) => {
        setSelectedFeedbackId(feedbackId);
        setViewMode('detail');
    }, []);

    const handleBackToList = useCallback(() => {
        setViewMode('list');
        setSelectedFeedbackId(null);
    }, []);

    const handleCreateSuccess = useCallback((message) => {
        setAlert({ message, type: 'success' });
        // Refresh the list
        if (refreshFn) {
            refreshFn();
        }
        window.dispatchEvent(new CustomEvent('feedbackListRefresh'));
    }, [refreshFn]);

    const handleRefresh = useCallback(() => {
        if (refreshFn) {
            refreshFn();
        }
        window.dispatchEvent(new CustomEvent('feedbackListRefresh'));
    }, [refreshFn]);

    const renderNewFeedbackButton = () => {
        return (
            <button
                onClick={() => setIsCreateModalOpen(true)}
                className="btn btn-outline-primary d-flex align-items-center gap-2 shadow"
            >
                <Plus size={20} />
                Neues Feedback
            </button>
        );
    };

    return (
        <div>
            <Alert
                message={alert.message}
                type={alert.type}
                onClose={() => setAlert({ message: '', type: '' })}
            />

            {viewMode === 'list' ? (
                <>
                    {/* Modern Header */}
                    <div className="mb-4">
                        <div className="d-flex flex-column flex-sm-row align-items-sm-center justify-content-between gap-3">
                            <div>
                                <h1 className="h2 fw-bold text-dark d-flex align-items-center gap-2 mb-2">
                                    <MessageSquarePlus size={32} className="icon-feedbacks" />
                                    Feedback
                                </h1>
                                <p className="text-muted mb-0">
                                    Teilen Sie Ihre Ideen, Vorschläge und Verbesserungswünsche
                                </p>
                            </div>
                            {renderNewFeedbackButton()}
                        </div>
                    </div>

                    <CardBasic>
                        <FeedbacksTabs
                            onViewDetail={handleViewDetail}
                            onRefresh={(fn) => setRefreshFn(() => fn)}
                        />
                    </CardBasic>
                </>
            ) : (
                <>
                    {/* Detail View Header */}
                    <div className="mb-4">
                        <h1 className="h2 fw-bold text-dark d-flex align-items-center gap-2 mb-2">
                            <MessageSquarePlus size={32} className="icon-feedbacks" />
                            Feedback
                        </h1>
                    </div>

                    <CardBasic>
                        <FeedbackDetail
                            feedbackId={selectedFeedbackId}
                            onBack={handleBackToList}
                            onRefresh={handleRefresh}
                        />
                    </CardBasic>
                </>
            )}

            <CreateFeedbackModal
                isOpen={isCreateModalOpen}
                onClose={() => setIsCreateModalOpen(false)}
                onSuccess={handleCreateSuccess}
            />
        </div>
    );
};

export default FeedbacksIndex;
